import { AbstractService, ApiResponse, PaginatedResponse } from './core/AbstractService';
import { Order, OrderStatus, PaymentStatus, FulfillmentStatus, Address, FinplusUser, FinplusSale } from './core/types';
import api from './core/api';

interface OrderStats {
  total: number;
  pending: number;
  confirmed: number;
  processing: number;
  shipped: number;
  delivered: number;
  cancelled: number;
  revenue: number;
}

interface RevenueData {
  labels: string[];
  data: number[];
  totalRevenue: number;
  averageOrderValue: number;
}

class OrderService extends AbstractService<Order> {
  constructor() {
    super('/admin/orders');
  }

  /**
   * Get order statistics
   */
  async getStats(): Promise<ApiResponse<OrderStats>> {
    const response = await api.get<ApiResponse<OrderStats>>(
      `${this.basePath}/stats`
    );
    return response.data;
  }

  /**
   * Get revenue analytics
   */
  async getRevenueAnalytics(
    dateFrom?: string,
    dateTo?: string
  ): Promise<ApiResponse<RevenueData>> {
    const params = new URLSearchParams();
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get<ApiResponse<RevenueData>>(
      `${this.basePath}/analytics/revenue${queryString}`
    );
    return response.data;
  }

  /**
   * Search orders
   */
  async searchOrders(
    query: string,
    params?: URLSearchParams
  ): Promise<PaginatedResponse<Order>> {
    const searchParams = params || new URLSearchParams();
    searchParams.set('q', query);

    const response = await api.get<PaginatedResponse<Order>>(
      `${this.basePath}/search?${searchParams.toString()}`
    );
    return response.data;
  }

  /**
   * Update order status
   */
  async updateStatus(
    orderId: string,
    status: OrderStatus,
    paymentStatus?: PaymentStatus,
    fulfillmentStatus?: FulfillmentStatus
  ): Promise<ApiResponse<Order>> {
    const response = await api.patch<ApiResponse<Order>>(
      `${this.basePath}/${orderId}/status`,
      {
        status,
        paymentStatus,
        fulfillmentStatus,
      }
    );
    return response.data;
  }

  /**
   * Cancel order
   */
  async cancelOrder(
    orderId: string,
    reason: string
  ): Promise<ApiResponse<Order>> {
    const response = await api.patch<ApiResponse<Order>>(
      `${this.basePath}/${orderId}/cancel`,
      { reason }
    );
    return response.data;
  }

  /**
   * Process order
   */
  async processOrder(
    orderId: string
  ): Promise<ApiResponse<{ order: Order; document?: Blob; documentUrl?: string }>> {
    const response = await api.post<ApiResponse<{ order: Order; document?: Blob; documentUrl?: string }>>(
      `${this.basePath}/${orderId}/process`
    );
    return response.data;
  }

  /**
   * Reject order
   */
  async rejectOrder(
    orderId: string,
    reason: string
  ): Promise<ApiResponse<Order>> {
    const response = await api.patch<ApiResponse<Order>>(
      `${this.basePath}/${orderId}/reject`,
      { reason }
    );
    return response.data;
  }

  /**
   * Update order shipping address
   */
  async updateShippingAddress(
    orderId: string,
    shippingAddress: Address
  ): Promise<ApiResponse<Order>> {
    const response = await api.patch<ApiResponse<Order>>(
      `${this.basePath}/${orderId}/shipping-address`,
      { shippingAddress }
    );
    return response.data;
  }

  /**
   * Mark order as not picked up (in-store pickup no-show)
   */
  async markNotPickedUp(
    orderId: string,
    reason: string
  ): Promise<ApiResponse<Order>> {
    const response = await api.patch<ApiResponse<Order>>(
      `${this.basePath}/${orderId}/not-picked-up`,
      { reason }
    );
    return response.data;
  }

  /**
   * List FinPlus staff users (shop 7 "Chicky"), for attributing a Create Sale action
   */
  async getFinplusUsers(): Promise<ApiResponse<FinplusUser[]>> {
    const response = await api.get<ApiResponse<FinplusUser[]>>(
      `${this.basePath}/finplus-users`
    );
    return response.data;
  }

  /**
   * Create a real, finalized sale in FinPlus for this order, attributed to the given staff user
   */
  async createFinplusSale(
    orderId: string,
    finplusUserId: number
  ): Promise<ApiResponse<{ order: Order; sale: FinplusSale }>> {
    const response = await api.post<ApiResponse<{ order: Order; sale: FinplusSale }>>(
      `${this.basePath}/${orderId}/create-sale`,
      { finplusUserId }
    );
    return response.data;
  }

  /**
   * Archive order
   */
  async archiveOrder(orderId: string): Promise<ApiResponse<Order>> {
    const response = await api.patch<ApiResponse<Order>>(
      `${this.basePath}/${orderId}/archive`
    );
    return response.data;
  }

  /**
   * Get archived orders
   */
  async getArchived(
    params?: URLSearchParams
  ): Promise<PaginatedResponse<Order>> {
    const queryString = params ? `?${params.toString()}` : '';
    const response = await api.get<PaginatedResponse<Order>>(
      `${this.basePath}/archived${queryString}`
    );
    return response.data;
  }
}

export const orderService = new OrderService();
export default orderService;
